import * as layerTypes from '../../../map/utils/layerTypes';
import * as geomTypes from '../../../map/utils/geomTypes';
import * as eventTypes from '../../../map/utils/eventTypes';
import * as styleTypes from '../../../map/utils/style/styleTypes';
import Draw from 'ol/interaction/Draw';
import { Fill, RegularShape, Stroke, Style, Text } from 'ol/style.js';
import { primaryAction } from 'ol/events/condition';

export default class AnnotationControl {
    constructor(resourcesHandler, classId, mapControl) {
        this.resourcesHandler = resourcesHandler;
        this.classId = classId;
        this.mapControl = mapControl;
        this.map = mapControl.map;
        this.currentInteraction = null;
        this.layerAdded = false;
        this.currentId = 1;
        this.callback = null;
    }

    init() {
        $event.register(eventTypes.START_ANNOTATION, this._getStartHandler(), this, this.mapControl.classId);
        $event.register(eventTypes.STOP_ANNOTATION, this._getStopHandler(), this, this.mapControl.classId);
    }

    _getStartHandler() {
        return (eventName, data, sender) => {
            this.callback = data && data.callback ? data.callback : null;
            this.targetLayerId = data && data.layerId ? data.layerId : null;

            if (!this.targetLayerId && data && data.layerName) {
                let layers = this.mapControl.layerSettingsHandler.getLayerSettingsByName(data.layerName.trim());
                if (layers != null && layers.length > 0) {
                    this.targetLayerId = layers[0].Id;
                }
            }

            this._start();
        };
    }

    _getStopHandler() {
        return (eventName, data, sender) => {
            this._stop();
        };
    }

    _start() {
        this.mapControl.selectionHandler.isActive = false;

        if (!this.layerAdded) {
            this._addLayer().then(() => this._addInteraction());
        } else {
            this._addInteraction();
        }
    }

    _stop() {
        if (this.currentInteraction) {
            this.map.removeInteraction(this.currentInteraction);
            this.currentInteraction = null;
        }
        let layer = this.mapControl.layerHandler.getLayerById(layerTypes.ANNOTATION);
        if (layer && layer.length > 0) {
            layer[0].set('selectable', true);
        }
        this.mapControl.selectionHandler.isActive = true;
        $event.notify(eventTypes.STOP_SNAP, {}, this, this.mapControl.classId);
        this.callback = null;
        this.targetLayerId = null;
    }

    _addLayer() {
        this.layerAdded = true;
        return this.mapControl.addTemporaryVectorLayer({
            name: layerTypes.ANNOTATION,
            geometryType: geomTypes.GEOM_POINT,
            srid: this.mapControl.generalSettingsHandler.settings.ProjCode,
            geomField: 'geom',
            idField: 'id',
            visible: true,
            displayName: this.resourcesHandler.get('Annotations'),
            ignoreStyle: true,
            addToLegend: true,
            parentId: layerTypes.INTERACTION_TOOLS,
            parentDisplayName: this.resourcesHandler.get('UserInteractionResults'),
            showLegend: false,
            declutter: false,
            selectable: true
        }).then(() => {
            let layer = this.mapControl.layerHandler.getLayerById(layerTypes.ANNOTATION)[0];
            layer.setStyle((feature) => this._styleFunction(feature));
        });
    }

    _addInteraction() {
        let layer = this.mapControl.layerHandler.getLayerById(layerTypes.ANNOTATION)[0];
        layer.set('selectable', false);
        let source = layer.getSource();

        let drawCondition = (evt) => {
            if (!primaryAction(evt)) return false;
            if (!this.targetLayerId) return true;
            let snapControl = this.mapControl.generalActionHandler && this.mapControl.generalActionHandler.generalActionControls
                ? this.mapControl.generalActionHandler.generalActionControls.snapControl
                : null;
            let tolerance = snapControl ? snapControl.currentSnapTolerance : 10;
            return this.map.hasFeatureAtPixel(evt.pixel, {
                hitTolerance: tolerance,
                layerFilter: (l) => l.get('uid') === this.targetLayerId
            });
        };

        this.currentInteraction = new Draw({
            source: source,
            type: 'Point',
            style: (feature) => this._drawStyleFunction(feature),
            condition: drawCondition,
            finishCondition: drawCondition
        });

        this.currentInteraction.on('drawend', (evt) => {
            let coords = evt.feature.getGeometry().getCoordinates();
            evt.feature.set('label', this._formatLabel(coords));
            evt.feature.set('id', this.currentId);
            evt.feature.layerId = layerTypes.ANNOTATION;
            this.currentId++;

            if (this.callback) {
                this.callback(evt.feature);
            }
        });

        this.map.addInteraction(this.currentInteraction);
        $event.notify(eventTypes.START_SNAP, { geomType: 'Point' }, this, this.mapControl.classId);
    }

    _formatLabel(coords) {
        return coords[0].toFixed(4) + ', ' + coords[1].toFixed(4);
    }

    _drawStyleFunction(feature) {
        return [];
    }

    _styleFunction(feature) {
        let xstate = feature.get('xstate');
        let isActive = xstate == styleTypes.SELECT_STYLE_TYPE || xstate == styleTypes.HOVER_STYLE_TYPE;
        let mainColor = isActive ? 'rgba(0, 200, 0, 1)' : 'rgba(255, 0, 255, 1)';
        return this._buildStyles(feature, mainColor);
    }

    _buildStyles(feature, mainColor) {

        return [
            new Style({
                image: new RegularShape({
                    stroke: new Stroke({ color: 'rgba(255, 255, 255, 1)', width: 5 }),
                    points: 4,
                    radius: 8,
                    radius2: 0,
                    angle: 0
                })
            }),
            new Style({
                image: new RegularShape({
                    stroke: new Stroke({ color: mainColor, width: 2 }),
                    points: 4,
                    radius: 8,
                    radius2: 0,
                    angle: 0
                }),
                text: new Text({
                    text: feature.get('label') || '',
                    font: '12px sans-serif',
                    offsetY: -14,
                    fill: new Fill({ color: 'rgba(0, 0, 0, 1)' }),
                    stroke: new Stroke({ color: 'rgba(255, 255, 255, 1)', width: 3 })
                })
            })
        ];
    }
}
